/**
 * Location-level Orbi SSID list utilities
 * Pure utility module — no React dependencies.
 *
 * When a location has no AP, the location Wi-Fi > SSID page lists the SSIDs of its Orbi
 * devices read-only. The location SSID list API adds `isAPPresent` and
 * `orbiWirelessNetworks` (one entry per Orbi device) when the request carries includeOrbi=1.
 */

/** Query params that ask the location SSID list API for the Orbi SSIDs. */
export const ORBI_SSID_LIST_REQUEST = { includeOrbi: '1' };

/** Orbi SSID slots, in the order (and with the slot numbers) the Orbi device WiFi Networks tab lists them. */
const ORBI_SSID_SLOTS = [
  { key: 'deviceWlanInfo', type: 'Admin', slotNo: '1' },
  { key: 'deviceGuestWlanInfo', type: 'Employee', slotNo: '2' },
  { key: 'deviceGuestPortalWlanInfo', type: 'Guest', slotNo: '3' },
  { key: 'deviceIOTWlanInfo', type: 'IoT', slotNo: '4' }
];

/**
 * Whether the SSID list response is for a location without AP that has at least one Orbi SSID,
 * checked without building the rows.
 */
export const hasOrbiSsids = (data) =>
  data?.isAPPresent === '0' &&
  Array.isArray(data?.orbiWirelessNetworks) &&
  data.orbiWirelessNetworks.some((orbiSsidList) => ORBI_SSID_SLOTS.some(({ key }) => orbiSsidList?.[key]));

/**
 * Flattens the Orbi SSIDs of a location into grid rows, one row per SSID, disabled ones included.
 * Returns an empty list when the location has an AP or no Orbi SSIDs, so the page keeps the
 * AP SSID list.
 */
export const getOrbiSsidRows = (data) => {
  if (data?.isAPPresent !== '0' || !Array.isArray(data?.orbiWirelessNetworks)) return [];

  return data.orbiWirelessNetworks.flatMap((orbiSsidList) => {
    const deviceInfo = orbiSsidList?.deviceInfo || {};
    return ORBI_SSID_SLOTS.filter(({ key }) => orbiSsidList?.[key]).map(({ key, type, slotNo }) => {
      const ssidInfo = orbiSsidList[key];
      return {
        // Unique across devices (every Orbi has the same four slots) and, like the AP list's
        // wirelessNetworkId, free of words, so the grid search only matches what the columns show
        id: `${deviceInfo.deviceId || deviceInfo.serialNo}-${slotNo}`,
        deviceName: deviceInfo.deviceName || deviceInfo.serialNo || '',
        ssidName: ssidInfo.ssidName || '',
        status: ssidInfo.enable == '1' ? 'Enabled' : 'Disabled',
        type: ssidInfo.type || type,
        security: ssidInfo.authentication || '--',
        vlan: ssidInfo.vlanProfile || 'N/A'
      };
    });
  });
};
